/**
 * Phase Core Atoms - Base state management for phases
 *
 * NO LOCAL TYPES - All types come from types/api.ts
 * Phases are stored separately from workflows for optimal re-rendering
 */

import { atom, type Atom } from "jotai";
import type { Phase } from "@/isomorphic/schemas";
import type { AtomStatus, AtomError } from "@/frontend/types/atoms";

// ============================================================================
// BASE ATOMS (Private)
// ============================================================================

/**
 * Internal state atom: All phases (raw state)
 * Private - do not use directly
 */
const _phasesStateAtom = atom<Phase[]>([]);

/**
 * Internal state atom: Phases as they exist in DB
 * Used by auto-save to detect DELETE operations
 * Updated after successful CREATE/UPDATE/DELETE operations
 */
export const _phasesInDBAtom = atom<Phase[]>([]);

/**
 * Internal atom: All phases (pure state)
 *
 * This is a simple state atom. Side-effects (history, auto-save) are handled
 * by action atoms in actions/phase-actions.ts
 *
 * Private - use phasesAtom for read access
 */
export const _phasesAtom = atom(
  (get) => get(_phasesStateAtom),
  (get, set, newPhases: Phase[]) => {
    set(_phasesStateAtom, newPhases);
  },
);

// ============================================================================
// PUBLIC READ-ONLY ATOMS
// ============================================================================

/**
 * Public atom: All phases (read-only)
 */
export const phasesAtom = atom((get) => get(_phasesAtom));

/**
 * Derived atom: Phases for a specific workflow
 * Uses a Map to cache atoms by workflowId to prevent creating new atoms on every call
 */
const workflowPhasesAtomCache = new Map<string, Atom<Phase[]>>();

export const workflowPhasesAtomFamily = (workflowId: string): Atom<Phase[]> => {
  if (!workflowPhasesAtomCache.has(workflowId)) {
    workflowPhasesAtomCache.set(
      workflowId,
      atom((get) => {
        const phases = get(_phasesAtom);
        return phases.filter((p) => p.workflowId === workflowId).sort((a, b) => a.orderIndex - b.orderIndex);
      }),
    );
  }
  return workflowPhasesAtomCache.get(workflowId)!;
};

// ============================================================================
// CURRENT PHASE ATOMS
// ============================================================================

/**
 * Current phase ID
 */
export const currentPhaseIdAtom = atom<string | null>(null);

/**
 * Current phase (derived from currentPhaseIdAtom)
 */
export const currentPhaseAtom = atom((get) => {
  const id = get(currentPhaseIdAtom);
  if (!id) return null;

  const phases = get(_phasesAtom);
  return phases.find((p) => p.id === id) || null;
});

// ============================================================================
// STATUS & ERROR ATOMS
// ============================================================================

/**
 * Loading status for phases
 */
export const phasesStatusAtom = atom<AtomStatus>("init");

/**
 * Loading status for workflow phases
 */
export const workflowPhasesStatusAtom = atom<AtomStatus>("init");

/**
 * Loading status for current phase
 */
export const currentPhaseStatusAtom = atom<AtomStatus>("init");

/**
 * Error for phases
 */
export const phasesErrorAtom = atom<AtomError>(null);

/**
 * Error for workflow phases
 */
export const workflowPhasesErrorAtom = atom<AtomError>(null);

/**
 * Error for current phase
 */
export const currentPhaseErrorAtom = atom<AtomError>(null);
